/**
 * AcquisitionIntelTab — property workspace tab for BP-027.
 *
 * Compact surface of the seller intelligence engines. Shows motivation
 * + acquisition scores, heat, next best action, contact recommendation,
 * and the explainability trail — all inline on the property workspace.
 * Deep-links to the full `/v3/intel/acquisition-intel` page for context.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, Flame, Loader2, RefreshCw, Phone, MessageSquare, Mail,
  Send, Archive, ArrowUpRight, Sparkles,
} from "lucide-react";

const HEAT_META = {
  hot:    { label: "HOT",    bg: "bg-red-500/15",    text: "text-red-500" },
  high:   { label: "HIGH",   bg: "bg-orange-500/15", text: "text-orange-500" },
  medium: { label: "MEDIUM", bg: "bg-amber-500/15",  text: "text-amber-500" },
  low:    { label: "LOW",    bg: "bg-sky-500/15",    text: "text-sky-500" },
  cold:   { label: "COLD",   bg: "bg-slate-500/15",  text: "text-slate-500" },
};

const ACTION_ICON = {
  call_now: Phone, call_this_week: Phone, send_sms: MessageSquare,
  send_email: Mail, send_offer: Send, send_direct_mail: Send,
  add_to_drip: Sparkles, assign_to_specialist: Sparkles,
  archive_cold: Archive, schedule_visit: Sparkles,
};

export default function AcquisitionIntelTab({ property }) {
  const propertyId = property?.id;
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalcing, setRecalcing] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/acquisition-intel/${propertyId}`);
      setIntel(data);
    } catch {
      setIntel(null);
    } finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const recalc = useCallback(async () => {
    if (!propertyId) return;
    setRecalcing(true);
    try {
      await api.post("/v3/acquisition-intel/recalculate", { property_id: propertyId });
      await load();
      toast.success("Acquisition intel refreshed.");
    } catch { toast.error("Refresh failed."); }
    finally { setRecalcing(false); }
  }, [propertyId, load]);

  if (loading) {
    return <div className="flex items-center justify-center py-16">
      <Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }
  if (!intel) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center"
           data-testid="acq-intel-tab-empty">
        <Brain className="w-8 h-8 text-emerald-500 mx-auto" />
        <h3 className="mt-3 text-base font-bold text-slate-900">No acquisition intelligence yet</h3>
        <p className="mt-1 text-sm text-slate-500">Run recalculation to score this seller.</p>
        <button type="button" onClick={recalc} disabled={recalcing}
                data-testid="acq-intel-tab-recalc"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 disabled:opacity-60"
                style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}>
          {recalcing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Score this seller
        </button>
      </div>
    );
  }

  const heat = HEAT_META[intel.heat] || HEAT_META.cold;
  const ActionIcon = ACTION_ICON[intel.next_action.kind] || Sparkles;

  return (
    <div className="space-y-4" data-testid="acq-intel-tab">
      {/* Score row + deep link */}
      <div className="grid md:grid-cols-3 gap-3">
        <ScoreTile label="Motivation" score={intel.motivation.total} band={intel.motivation.band} />
        <ScoreTile label="Acquisition" score={intel.acquisition.total} band={intel.acquisition.band}
                    right={<span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${heat.bg} ${heat.text}`}><Flame className="w-2.5 h-2.5" />{heat.label}</span>} />
        <div className="rounded-xl border-2 border-amber-300/60 bg-amber-50 p-4">
          <div className="text-[10px] font-black tracking-widest uppercase text-amber-700">Next best action</div>
          <div className="mt-1 flex items-center gap-2">
            <ActionIcon className="w-4 h-4 text-amber-600" />
            <div className="text-base font-bold text-slate-900 leading-tight">{intel.next_action.label}</div>
          </div>
          <p className="mt-1 text-[11px] text-slate-600 leading-snug">{intel.next_action.reasoning}</p>
        </div>
      </div>

      {/* Contact rec */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">Recommended Contact</div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest">
            {intel.contact.method.replace(/_/g, " ")}
          </span>
          <span className="text-[11px] font-semibold text-slate-500">{intel.contact.window}</span>
        </div>
        {intel.contact.ai_talking_points?.length > 0 && (
          <ul className="mt-2 space-y-1 pl-4 list-disc text-[12px] text-slate-700 leading-relaxed">
            {intel.contact.ai_talking_points.slice(0, 3).map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        )}
      </div>

      {/* Explainability collapse */}
      <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="acq-intel-tab-explain">
        <summary className="text-[10px] font-black tracking-widest uppercase text-emerald-600 cursor-pointer">
          Why this score — {intel.motivation.reasons?.length || 0} reasons
        </summary>
        <ul className="mt-3 space-y-1.5">
          {intel.motivation.reasons?.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-[13px]">
              <span className={`shrink-0 w-9 text-right font-bold tabular-nums ${r.weight >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {r.weight >= 0 ? `+${r.weight}` : r.weight}
              </span>
              <span className="text-slate-700">{r.label}</span>
            </li>
          ))}
        </ul>
      </details>

      <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-100">
        <button type="button" onClick={recalc} disabled={recalcing}
                data-testid="acq-intel-tab-recalc"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold px-3 py-1.5 hover:bg-slate-50 disabled:opacity-60">
          {recalcing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
        <Link to="/v3/intel/acquisition-intel"
              data-testid="acq-intel-tab-deeplink"
              className="inline-flex items-center gap-1.5 rounded-lg text-emerald-700 text-xs font-bold px-3 py-1.5 hover:bg-emerald-50">
          Open Acquisition Intelligence workspace <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

function ScoreTile({ label, score, band, right }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-black tracking-widest uppercase text-slate-500">{label}</div>
        {right}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-3xl font-bold text-slate-900 tabular-nums">{score}</div>
        <div className="text-xs text-slate-400 font-semibold uppercase tracking-widest">/ 100</div>
      </div>
      <div className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{band}</div>
    </div>
  );
}
